/**
 * A router keyed on screen ids from product/screens.json. Routes are declared
 * once here; nothing else in the app builds a URL by hand, so a screen id is
 * the only address the codebase knows.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
  type MouseEvent,
} from 'react'
import { canNavigate, screenById, type RoutableScreenId, type ScreenId } from './contract'
import { bridge } from './bridge'

export type RouteParams = Record<string, string>

interface RouteDef {
  screen: RoutableScreenId
  /** `:name` segments become params. Order matters: static paths win. */
  pattern: string
}

const routes: RouteDef[] = [
  { screen: 'welcome', pattern: '/welcome' },
  { screen: 'signIn', pattern: '/sign-in' },
  { screen: 'authCollision', pattern: '/auth-collision' },
  { screen: 'betaAccess', pattern: '/beta-access' },
  { screen: 'dashboard', pattern: '/dashboard' },
  { screen: 'upload', pattern: '/upload' },
  { screen: 'importReview', pattern: '/import/:statementId' },
  { screen: 'accounts', pattern: '/accounts' },
  { screen: 'accountDetail', pattern: '/accounts/:id' },
  { screen: 'transactions', pattern: '/transactions' },
  { screen: 'transactionDetail', pattern: '/transactions/:id' },
  { screen: 'categories', pattern: '/categories' },
  { screen: 'categoryDetail', pattern: '/categories/:id' },
  { screen: 'recurring', pattern: '/recurring' },
  { screen: 'recurringDetail', pattern: '/recurring/:id' },
  { screen: 'fees', pattern: '/fees' },
  { screen: 'cashFlow', pattern: '/cash-flow' },
  { screen: 'debtPlanner', pattern: '/debt/planner' },
  { screen: 'debt', pattern: '/debt' },
  { screen: 'savings', pattern: '/savings' },
  { screen: 'healthScore', pattern: '/health-score' },
  { screen: 'recommendations', pattern: '/recommendations' },
  { screen: 'recommendationDetail', pattern: '/recommendations/:id' },
  { screen: 'progress', pattern: '/progress' },
  { screen: 'settings', pattern: '/settings' },
  { screen: 'signInMethods', pattern: '/settings/sign-in-methods' },
  { screen: 'reauthenticate', pattern: '/settings/reauthenticate' },
  { screen: 'export', pattern: '/export' },
  { screen: 'privacy', pattern: '/privacy' },
  { screen: 'connectedAccounts', pattern: '/banks' },
  { screen: 'linkAccount', pattern: '/banks/link' },
  { screen: 'linkError', pattern: '/banks/link/error' },
  { screen: 'linkSuccess', pattern: '/banks/:id' },
]

const byScreen = new Map<RoutableScreenId, RouteDef>(routes.map((r) => [r.screen, r]))

export function pathFor(screen: RoutableScreenId, params: RouteParams = {}, query?: RouteParams): string {
  const route = byScreen.get(screen)
  if (!route) throw new Error(`No route for screen "${screen}"`)
  const path = route.pattern.replace(/:(\w+)/g, (_, name: string) => {
    const value = params[name]
    if (value === undefined) throw new Error(`Route ${screen} needs param "${name}"`)
    return encodeURIComponent(value)
  })
  const search = query && Object.keys(query).length ? `?${new URLSearchParams(query)}` : ''
  return path + search
}

export interface Route {
  screen: RoutableScreenId
  params: RouteParams
  query: URLSearchParams
}

function match(pathname: string, search: string): Route | null {
  const parts = pathname.replace(/\/+$/, '').split('/').filter(Boolean)
  for (const route of routes) {
    const segs = route.pattern.split('/').filter(Boolean)
    if (segs.length !== parts.length) continue
    const params: RouteParams = {}
    let ok = true
    for (let i = 0; i < segs.length; i++) {
      const seg = segs[i]
      if (seg.startsWith(':')) params[seg.slice(1)] = decodeURIComponent(parts[i])
      else if (seg !== parts[i]) {
        ok = false
        break
      }
    }
    if (ok) return { screen: route.screen, params, query: new URLSearchParams(search) }
  }
  return null
}

interface RouterValue {
  route: Route
  navigate: (screen: RoutableScreenId, params?: RouteParams, options?: NavigateOptions) => void
  back: () => void
}

export interface NavigateOptions {
  replace?: boolean
  query?: RouteParams
}

const RouterContext = createContext<RouterValue | null>(null)

export function RouterProvider({ fallback, children }: { fallback: RoutableScreenId; children: ReactNode }) {
  const [route, setRoute] = useState<Route>(
    () => match(location.pathname, location.search) ?? { screen: fallback, params: {}, query: new URLSearchParams(location.search) },
  )

  useEffect(() => {
    const onPop = () => {
      setRoute(
        match(location.pathname, location.search) ?? {
          screen: fallback,
          params: {},
          query: new URLSearchParams(location.search),
        },
      )
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [fallback])

  const navigate = useCallback(
    (screen: RoutableScreenId, params: RouteParams = {}, options: NavigateOptions = {}) => {
      if (import.meta.env.DEV && !canNavigate(route.screen, screen) && screen !== route.screen) {
        bridge.log(
          `router: ${route.screen} -> ${screen} is not an edge in screens.json. Add it to navigatesTo or use a different route.`,
        )
      }
      const url = pathFor(screen, params, options.query)
      if (options.replace) history.replaceState(null, '', url)
      else history.pushState(null, '', url)
      setRoute({
        screen,
        params,
        query: new URLSearchParams(options.query ?? {}),
      })
      window.scrollTo({ top: 0 })
    },
    [route.screen],
  )

  const back = useCallback(() => history.back(), [])

  const value = useMemo<RouterValue>(() => ({ route, navigate, back }), [route, navigate, back])
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>
}

function useRouter(): RouterValue {
  const value = useContext(RouterContext)
  if (!value) throw new Error('useRouter outside RouterProvider')
  return value
}

export function useRoute(): Route {
  return useRouter().route
}

export function useNavigate() {
  return useRouter().navigate
}

export function useBack() {
  return useRouter().back
}

/** Redirect without leaving a history entry. Used for the auth gate. */
export function useRedirect(to: RoutableScreenId | null, params?: RouteParams) {
  const navigate = useNavigate()
  const route = useRoute()
  useEffect(() => {
    if (to && route.screen !== to) navigate(to, params, { replace: true })
  }, [to, route.screen, navigate, params])
}

interface LinkProps {
  screen: RoutableScreenId
  params?: RouteParams
  query?: RouteParams
  className?: string
  children: ReactNode
  title?: string
  'aria-label'?: string
}

export function Link({ screen, params, query, className, children, ...rest }: LinkProps) {
  const navigate = useNavigate()
  const href = pathFor(screen, params, query)
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return
    e.preventDefault()
    navigate(screen, params, { query })
  }
  return (
    <a href={href} className={className} onClick={onClick} {...rest}>
      {children}
    </a>
  )
}

export function titleFor(screen: ScreenId): string {
  return screenById.get(screen)?.title ?? screen
}
